// ============================================================================
// Troubleshooting Guide — SINGLE SOURCE OF TRUTH (cloud frontend copy)
// ============================================================================
//
// Backs BOTH the in-app Guide/Help UI (modal + /guide page) AND the rule-based
// matcher (matchGuideTopic.ts). Edit the text here once and both update.
//
// ⚠️ KEEP IN SYNC with the agent app's copy:
//    pro_da_agent/cmd/prodaagent/frontend/src/lib/guide/guideContent.ts
//
// Bilingual: every user-facing string has `en` (English) + `hi` (proper Hindi /
// Devanagari). Technical tokens (RTSP, IP, port, FPS, I-Frame, GOP, H.264, TCP…)
// stay Latin inside the Hindi text — the professional norm, and it matches what
// operators see in their camera's menus.
//
// Manual-guide formatting:
//   • Steps are { title, detail } — a bold action title + a short explanation,
//     the way a real manual reads (not a flat numbered list).
//   • Wrap inline technical bits in `backticks` — the renderer shows them as
//     monospace code chips (IPs, ports, URLs, commands).
//   • `notes` render as coloured callout boxes: 'tip' (do this), 'note' (FYI),
//     'warn' (careful).
// ============================================================================

export type GuideLang = 'en' | 'hi';

export type LangText = Record<GuideLang, string>;

/** One step in a how-to: a bold action title + an explanation. */
export interface GuideStep {
  title: LangText;
  detail: LangText;
}

/** A callout box rendered after the steps. */
export interface GuideNote {
  kind: 'tip' | 'note' | 'warn';
  text: LangText;
}

export interface GuideTable {
  headers: Record<GuideLang, string[]>;
  rows: Record<GuideLang, string[][]>;
}

export interface GuideTopic {
  id:
    | 'connect-camera'
    | 'rtsp-url'
    | 'fps'
    | 'connect-errors'
    | 'lag'
    | 'checklist';
  /** Match keywords — English + Hinglish (roman) + Hindi (Devanagari). */
  keywords: string[];
  title: LangText;
  intro?: LangText;
  symptom?: LangText;
  cause?: LangText;
  steps?: GuideStep[];
  notes?: GuideNote[];
  /** Copy-paste config/example (language-agnostic). */
  snippet?: string;
  table?: GuideTable;
}

// ── RTSP paths by brand (language-agnostic). Appended to rtsp://USER:PASS@IP:554
const RTSP_ROWS: string[][] = [
  ['CP PLUS', '/cam/realmonitor?channel=1&subtype=0', '/cam/realmonitor?channel=1&subtype=1'],
  ['Dahua', '/cam/realmonitor?channel=1&subtype=0', '/cam/realmonitor?channel=1&subtype=1'],
  ['Hikvision', '/Streaming/Channels/101', '/Streaming/Channels/102'],
  ['Hikvision DVR ch.N', '/Streaming/Channels/N01', '/Streaming/Channels/N02'],
  ['Axis', '/axis-media/media.amp', '/axis-media/media.amp?streamprofile=sub'],
  ['Reolink', '/h264Preview_01_main', '/h264Preview_01_sub'],
  ['Amcrest', '/cam/realmonitor?channel=1&subtype=0', '/cam/realmonitor?channel=1&subtype=1'],
  ['Uniview', '/unicast/c1/s0/live', '/unicast/c1/s1/live'],
  ['Generic ONVIF', '/stream1  (or /onvif1)', '/stream2  (or /onvif2)'],
];

export const GUIDE_TOPICS: GuideTopic[] = [
  // ── Topic 1 — Connect a camera (onboarding) ───────────────────────────────
  {
    id: 'connect-camera',
    keywords: [
      'connect', 'add camera', 'new camera', 'setup', 'install', 'how to connect',
      'camera add', 'kaise connect', 'camera kaise', 'add kaise',
      'कनेक्ट', 'कैमरा जोड़', 'सेटअप', 'कैसे जोड़',
    ],
    title: {
      en: 'How to connect a CCTV camera',
      hi: 'CCTV कैमरा कैसे कनेक्ट करें',
    },
    intro: {
      en: 'ProWatchly’s on-prem agent connects to your camera over the LOCAL network — the cloud never talks to the camera directly. So the camera only has to be reachable from the PC running the agent. Adding one takes about 5 minutes.',
      hi: 'ProWatchly का on-prem agent आपके कैमरे से LOCAL network के ज़रिए जुड़ता है — cloud कभी सीधे कैमरे से बात नहीं करता। इसलिए कैमरा सिर्फ़ उस PC से reachable होना चाहिए जिस पर agent चल रहा है। एक कैमरा जोड़ने में लगभग 5 मिनट लगते हैं।',
    },
    steps: [
      {
        title: { en: 'Find the camera on your network', hi: 'नेटवर्क पर कैमरा ढूँढें' },
        detail: {
          en: 'Note its IP address (e.g. `192.168.1.64`), the RTSP port (almost always `554`), and the username + password set on the camera. The IP is usually in your router’s device list or the camera’s own app.',
          hi: 'उसका IP address (जैसे `192.168.1.64`), RTSP port (लगभग हमेशा `554`), और कैमरे पर सेट किया गया username + password नोट करें। IP आम तौर पर router की device list या कैमरे की अपनी app में मिल जाता है।',
        },
      },
      {
        title: { en: 'Confirm the agent PC can reach it', hi: 'पुष्टि करें कि agent PC उस तक पहुँच सकता है' },
        detail: {
          en: 'From the PC running the agent, run `ping 192.168.1.64`. Replies = good. No reply = the camera and PC are on different networks, or the IP is wrong — fix that first.',
          hi: 'agent वाले PC पर `ping 192.168.1.64` चलाएँ। reply आए = ठीक है। reply न आए = कैमरा और PC अलग-अलग network पर हैं, या IP ग़लत है — पहले उसे ठीक करें।',
        },
      },
      {
        title: { en: 'Build the RTSP URL', hi: 'RTSP URL बनाएँ' },
        detail: {
          en: 'This is the address the agent pulls video from. Format: `rtsp://username:password@IP:554/<path>`. The `<path>` depends on your brand — open the “RTSP connection string” topic for CP PLUS, Hikvision, Dahua and more.',
          hi: 'यह वही address है जहाँ से agent video लेता है। फ़ॉर्मैट: `rtsp://username:password@IP:554/<path>`। `<path>` आपके brand पर निर्भर करता है — CP PLUS, Hikvision, Dahua आदि के लिए “RTSP connection string” टॉपिक देखें।',
        },
      },
      {
        title: { en: 'Add it in the ProWatchly dashboard', hi: 'ProWatchly dashboard में जोड़ें' },
        detail: {
          en: 'Go to Devices → “Add camera”. Give it a name, paste the RTSP URL, enter the username/password, and pick the AI type (intrusion, PPE, etc.).',
          hi: 'Devices → “Add camera” पर जाएँ। नाम दें, RTSP URL paste करें, username/password भरें, और AI type चुनें (intrusion, PPE, आदि)।',
        },
      },
      {
        title: { en: 'Save and wait ~30 seconds', hi: 'Save करें और ~30 सेकंड रुकें' },
        detail: {
          en: 'The agent picks up the new camera automatically and starts streaming. The dashboard tile shows a live snapshot as soon as the first frames arrive.',
          hi: 'agent नया कैमरा अपने-आप उठा लेता है और streaming शुरू कर देता है। पहले frames आते ही dashboard tile पर live snapshot दिखने लगता है।',
        },
      },
    ],
    notes: [
      {
        kind: 'tip',
        text: {
          en: 'Keep the camera’s IP, port, username and password handy before you start — then this is a 2-minute job.',
          hi: 'शुरू करने से पहले कैमरे का IP, port, username और password तैयार रखें — फिर यह 2 मिनट का काम है।',
        },
      },
      {
        kind: 'note',
        text: {
          en: 'If the tile stays blank or shows an error, open the “Camera won’t connect” topic.',
          hi: 'अगर tile blank रहे या error दिखे, तो “कैमरा connect नहीं हो रहा” टॉपिक खोलें।',
        },
      },
    ],
  },

  // ── Topic 2 — RTSP connection string (CP PLUS featured) ───────────────────
  {
    id: 'rtsp-url',
    keywords: [
      'rtsp', 'url', 'connection string', 'cp plus', 'cpplus', 'cp-plus',
      'hikvision', 'dahua', 'reolink', 'axis', 'amcrest', 'uniview', 'onvif',
      'channel', 'subtype', 'path', 'brand', 'url kaise', 'connection kaise',
      'सीपी प्लस', 'कनेक्शन स्ट्रिंग', 'यूआरएल', 'पाथ',
    ],
    title: {
      en: 'RTSP connection string (CP PLUS, Hikvision, Dahua…)',
      hi: 'RTSP connection string कैसे बनाएँ (CP PLUS, Hikvision, Dahua…)',
    },
    intro: {
      en: 'The RTSP URL tells the agent where to pull the video from. It’s always `rtsp://username:password@IP:port/path` — only the `/path` changes by brand. CP PLUS is the most common brand in India; its cameras and DVRs use the Dahua-style path.',
      hi: 'RTSP URL agent को बताता है कि video कहाँ से लेनी है। यह हमेशा `rtsp://username:password@IP:port/path` होता है — सिर्फ़ `/path` हर brand में बदलता है। भारत में CP PLUS सबसे आम brand है; इसके कैमरे और DVR Dahua-style path इस्तेमाल करते हैं।',
    },
    steps: [
      {
        title: { en: 'Start with the base', hi: 'base से शुरू करें' },
        detail: {
          en: '`rtsp://USERNAME:PASSWORD@IP:554/` — replace USERNAME/PASSWORD with the camera login and IP with the camera’s address.',
          hi: '`rtsp://USERNAME:PASSWORD@IP:554/` — USERNAME/PASSWORD की जगह कैमरे का login और IP की जगह कैमरे का address डालें।',
        },
      },
      {
        title: { en: 'Encode special characters in the password', hi: 'password के special characters encode करें' },
        detail: {
          en: 'If the password has `@ : / #`, URL-encode them (`@` → `%40`, `#` → `%23`, `:` → `%3A`). An un-encoded `@` in the password breaks the URL.',
          hi: 'अगर password में `@ : / #` हैं, तो उन्हें URL-encode करें (`@` → `%40`, `#` → `%23`, `:` → `%3A`)। password में बिना-encode किया हुआ `@` URL को तोड़ देता है।',
        },
      },
      {
        title: { en: 'Append the brand path', hi: 'brand का path जोड़ें' },
        detail: {
          en: 'Add your brand’s path from the table below. On a DVR/NVR, change the channel number to the camera you want (`channel=1, 2, 3…`, or `101 / 201 / 301` for Hikvision).',
          hi: 'नीचे दी गई table से अपने brand का path जोड़ें। DVR/NVR पर, जो कैमरा चाहिए उसका channel number बदलें (`channel=1, 2, 3…`, या Hikvision के लिए `101 / 201 / 301`)।',
        },
      },
      {
        title: { en: 'Pick main vs sub stream', hi: 'main या sub stream चुनें' },
        detail: {
          en: 'Use the MAIN stream for recording/evidence, and the SUB stream (lower resolution) for AI + live view to save upload bandwidth.',
          hi: 'Recording/evidence के लिए MAIN stream और AI + live view के लिए SUB stream (कम resolution) इस्तेमाल करें — इससे upload bandwidth बचती है।',
        },
      },
      {
        title: { en: 'Don’t know your model’s path?', hi: 'Model का path नहीं पता?' },
        detail: {
          en: 'Open the camera’s web page → Network / Stream settings → look for the “RTSP” URL, or use the free “ONVIF Device Manager” tool to auto-discover it. Confirm any URL by playing it in VLC (Media → Open Network Stream).',
          hi: 'कैमरे का web page खोलें → Network / Stream settings → “RTSP” URL देखें, या free “ONVIF Device Manager” tool से auto-discover करें। किसी भी URL को VLC में चलाकर जाँच लें (Media → Open Network Stream)।',
        },
      },
    ],
    notes: [
      {
        kind: 'tip',
        text: {
          en: 'CP PLUS (very common in India) uses the Dahua-style path — copy the example below and just change the IP, login and channel.',
          hi: 'CP PLUS (भारत में बहुत आम) Dahua-style path इस्तेमाल करता है — नीचे का example कॉपी करें और सिर्फ़ IP, login और channel बदलें।',
        },
      },
    ],
    snippet:
      '# CP PLUS camera — IP 192.168.1.108, user "admin", pass "Admin@123"\n' +
      '#   the @ in the password is encoded as %40\n' +
      'rtsp://admin:Admin%40123@192.168.1.108:554/cam/realmonitor?channel=1&subtype=0\n' +
      '\n' +
      '# CP PLUS DVR — channel 3, sub-stream (good for AI):\n' +
      'rtsp://admin:Admin%40123@192.168.1.108:554/cam/realmonitor?channel=3&subtype=1',
    table: {
      headers: {
        en: ['Brand', 'Main stream path', 'Sub stream path'],
        hi: ['Brand', 'Main stream path', 'Sub stream path'],
      },
      rows: { en: RTSP_ROWS, hi: RTSP_ROWS },
    },
  },

  // ── Topic 3 — FPS / I-Frame interval mismatch ─────────────────────────────
  {
    id: 'fps',
    keywords: [
      'fps', 'frame', 'frames', 'keyframe', 'gop', 'i-frame', 'iframe',
      'interval', 'mismatch', 'h.264+', 'h264+', 'h.265+', 'h265+',
      'smart codec', 'smartcodec', 'detection slow', 'inference slow',
      'slow detection', 'fps slow', 'fps kam', 'fps sahi nahi', 'frame slow',
      'एफपीएस', 'फ्रेम', 'धीमा', 'इंटरवल', 'कीफ्रेम',
    ],
    title: {
      en: 'FPS / AI interval mismatch (I-Frame interval / GOP)',
      hi: 'FPS / AI interval mismatch (I-Frame interval / GOP)',
    },
    symptom: {
      en: 'You set AI FPS to 2 (or more) but detection is slow, the snapshot updates only once every few seconds, or frames look frozen / grey.',
      hi: 'आपने AI FPS 2 (या ज़्यादा) सेट किया है, फिर भी detection धीमा है, snapshot हर कुछ सेकंड में सिर्फ़ एक बार update होता है, या frame frozen / grey दिखते हैं।',
    },
    cause: {
      en: 'AI analyses ONE frame per camera keyframe. If the I-Frame interval (GOP) is long — or H.264+ / H.265+ (Smart Codec) is ON, which stretches the GOP — keyframes arrive rarely, so the real AI rate falls far below your setting. The AI rate can never exceed the camera’s keyframe rate.',
      hi: 'AI हर keyframe पर सिर्फ़ एक frame analyse करता है। अगर I-Frame interval (GOP) लंबा है — या H.264+ / H.265+ (Smart Codec) ON है, जो GOP बढ़ा देता है — तो keyframe कम आते हैं, इसलिए असली AI rate आपकी setting से काफ़ी कम रह जाता है। AI rate कभी कैमरे के keyframe rate से ज़्यादा नहीं हो सकता।',
    },
    steps: [
      {
        title: { en: 'Open the encoding settings', hi: 'encoding settings खोलें' },
        detail: {
          en: 'Camera web UI → Configuration → Video / Encoding.',
          hi: 'कैमरे का web UI → Configuration → Video / Encoding।',
        },
      },
      {
        title: { en: 'Turn OFF Smart Codec', hi: 'Smart Codec OFF करें' },
        detail: {
          en: 'Disable `H.264+` / `H.265+` / Smart Codec and pick plain `H.264` (or `H.265`). Smart Codec overrides the I-Frame interval, so it MUST be off.',
          hi: '`H.264+` / `H.265+` / Smart Codec बंद करें और plain `H.264` (या `H.265`) चुनें। Smart Codec आपके I-Frame interval को override कर देता है, इसलिए इसका बंद होना ज़रूरी है।',
        },
      },
      {
        title: { en: 'Set the I-Frame interval', hi: 'I-Frame interval सेट करें' },
        detail: {
          en: 'Set I-Frame Interval (a.k.a. GOP / Key Frame Interval) to the frame rate, or half of it. Rule: `keyframes/sec = Frame Rate ÷ I-Frame Interval`. e.g. 25 fps with interval `12` ≈ 2 keyframes/sec.',
          hi: 'I-Frame Interval (यानी GOP / Key Frame Interval) को frame rate के बराबर या उसका आधा सेट करें। नियम: `keyframes/sec = Frame Rate ÷ I-Frame Interval`। जैसे 25 fps पर interval `12` ≈ 2 keyframes/सेकंड।',
        },
      },
      {
        title: { en: 'Use Constant Bitrate (CBR)', hi: 'Constant Bitrate (CBR) रखें' },
        detail: {
          en: 'Set Bitrate Type to `CBR` for steady, even frame timing.',
          hi: 'steady, समान frame timing के लिए Bitrate Type को `CBR` सेट करें।',
        },
      },
      {
        title: { en: 'Save and reconnect', hi: 'Save करें और reconnect करें' },
        detail: {
          en: 'Restart the stream and reconnect. The agent’s `[FPS]` log line shows the real rate (forwarded_fps / keyframes_seen) for confirmation.',
          hi: 'stream restart करके reconnect करें। पुष्टि के लिए agent की `[FPS]` log line असली rate (forwarded_fps / keyframes_seen) दिखाती है।',
        },
      },
    ],
    notes: [
      {
        kind: 'tip',
        text: {
          en: 'Best practice: don’t touch the MAIN stream. Point the “AI substream URL” at a low-resolution sub stream with a short I-Frame interval — the main stream then stays high-quality for recording.',
          hi: 'बेहतर तरीका: MAIN stream को मत छेड़ें। “AI substream URL” को कम resolution वाली sub stream (छोटे I-Frame interval के साथ) पर लगाएँ — तब main stream recording के लिए high-quality बना रहता है।',
        },
      },
    ],
    snippet:
      'keyframes/sec = Frame Rate ÷ I-Frame Interval\n' +
      '\n' +
      'Goal: ~2 fps AI on a 25 fps camera\n' +
      '  I-Frame Interval = 25 ÷ 2  ≈ 12   → ~2 keyframes/sec   (good)\n' +
      '  I-Frame Interval = 50             → 0.5 keyframes/sec  (too slow)',
  },

  // ── Topic 4 — Connect errors (401 / 404 / timeout) ────────────────────────
  {
    id: 'connect-errors',
    keywords: [
      'error', '401', '404', 'timeout', 'unauthorized', 'not found',
      'connect nahi', 'password galat', 'fail', 'unreachable', 'vlc',
      'ping', 'firewall', 'unknown error',
      'कनेक्ट नहीं', 'पासवर्ड', 'एरर', 'टाइमआउट',
    ],
    title: {
      en: 'Camera won’t connect (401 / 404 / timeout)',
      hi: 'कैमरा connect नहीं हो रहा (401 / 404 / timeout)',
    },
    symptom: {
      en: 'The camera won’t connect, or you see errors such as timeout, 401 Unauthorized, or 404 Not Found.',
      hi: 'कैमरा connect नहीं होता, या timeout, 401 Unauthorized, या 404 Not Found जैसे errors दिखते हैं।',
    },
    cause: {
      en: 'Each error points at a specific cause — match the error to the fix below.',
      hi: 'हर error किसी एक वजह की ओर इशारा करता है — नीचे error को उसके fix से मिलाएँ।',
    },
    steps: [
      {
        title: { en: 'timeout', hi: 'timeout' },
        detail: {
          en: '`ping` the camera IP from the agent PC. No reply = network/IP problem. Check the camera is powered on and port `554` isn’t blocked by a firewall.',
          hi: 'agent PC से कैमरे के IP को `ping` करें। reply न आना = network/IP की समस्या। देखें कि कैमरा on है और port `554` किसी firewall से block नहीं है।',
        },
      },
      {
        title: { en: '401 Unauthorized', hi: '401 Unauthorized' },
        detail: {
          en: 'Wrong username/password. If the password has special characters, URL-encode them (`@` → `%40`). Repeated wrong attempts can lock the camera — wait a few minutes or reboot it.',
          hi: 'ग़लत username/password। password में special character हों तो उन्हें URL-encode करें (`@` → `%40`)। बार-बार ग़लत कोशिश से कैमरा lock हो सकता है — कुछ मिनट रुकें या reboot करें।',
        },
      },
      {
        title: { en: '404 Not Found', hi: '404 Not Found' },
        detail: {
          en: 'Wrong RTSP path. Use the correct path for your brand from the “RTSP connection string” topic; on a DVR, double-check the channel number.',
          hi: 'ग़लत RTSP path। “RTSP connection string” टॉपिक से अपने brand का सही path लें; DVR पर channel number दोबारा जाँचें।',
        },
      },
      {
        title: { en: 'Still stuck? Test in VLC', hi: 'फिर भी अटका? VLC में टेस्ट करें' },
        detail: {
          en: 'Paste the exact URL into VLC (Media → Open Network Stream). If VLC can’t play it either, the URL or credentials are wrong — fix it there first, then add it to ProWatchly.',
          hi: 'वही exact URL VLC में डालें (Media → Open Network Stream)। अगर VLC में भी न चले, तो URL या credentials ग़लत हैं — पहले वहीं ठीक करें, फिर ProWatchly में जोड़ें।',
        },
      },
    ],
    notes: [
      {
        kind: 'tip',
        text: {
          en: 'Prefer TCP transport — it’s more reliable than UDP across networks.',
          hi: 'TCP transport इस्तेमाल करें — यह networks पर UDP से ज़्यादा reliable है।',
        },
      },
    ],
  },

  // ── Topic 5 — Lag / delay / buffering ─────────────────────────────────────
  {
    id: 'lag',
    keywords: [
      'lag', 'delay', 'buffer', 'buffering', 'latency', 'choppy', 'stutter',
      'freeze', 'freezing', 'slow', 'lag ho raha', 'delay aa raha',
      'ruk ruk', 'atak', 'अटक', 'देरी', 'लैग', 'बफर',
    ],
    title: {
      en: 'Frame lag / delay / buffering',
      hi: 'Frame lag / देरी / buffering',
    },
    symptom: {
      en: 'Live view or detection is delayed, choppy, or keeps buffering.',
      hi: 'Live view या detection में देरी होती है, अटक-अटक कर चलता है, या बार-बार buffer होता है।',
    },
    cause: {
      en: 'Resolution/bitrate too high for your upload bandwidth, an unreliable transport, or the agent device is overloaded.',
      hi: 'आपकी upload bandwidth के लिए resolution/bitrate बहुत ज़्यादा है, transport unreliable है, या agent device overload है।',
    },
    steps: [
      {
        title: { en: 'Lower resolution / bitrate', hi: 'resolution / bitrate कम करें' },
        detail: {
          en: 'Drop the camera to e.g. `1080p → 720p`, or use a sub stream for AI/live.',
          hi: 'कैमरे को जैसे `1080p → 720p` पर लाएँ, या AI/live के लिए sub stream इस्तेमाल करें।',
        },
      },
      {
        title: { en: 'Switch transport to TCP', hi: 'transport TCP करें' },
        detail: {
          en: 'TCP handles packet loss better than UDP across networks.',
          hi: 'TCP networks पर packet loss को UDP से बेहतर संभालता है।',
        },
      },
      {
        title: { en: 'Prefer a wired link', hi: 'wired link को प्राथमिकता दें' },
        detail: {
          en: 'A LAN cable between the camera and the agent is far steadier than Wi-Fi.',
          hi: 'कैमरे और agent के बीच LAN cable, Wi-Fi से कहीं ज़्यादा स्थिर रहता है।',
        },
      },
      {
        title: { en: 'Reduce load per device', hi: 'per-device load घटाएँ' },
        detail: {
          en: 'If one agent PC handles many cameras, lower the count per device or the AI FPS.',
          hi: 'अगर एक ही agent PC कई कैमरे संभाल रहा है, तो per-device कैमरों की संख्या या AI FPS कम करें।',
        },
      },
    ],
    notes: [
      {
        kind: 'tip',
        text: {
          en: 'For Watch Live, set a low-bitrate sub stream URL — live then uses about 10x less bandwidth.',
          hi: 'Watch Live के लिए low-bitrate sub stream URL सेट करें — तब live लगभग 10x कम bandwidth लेता है।',
        },
      },
    ],
  },

  // ── Topic 6 — Recommended settings checklist (table) ──────────────────────
  {
    id: 'checklist',
    keywords: [
      'checklist', 'recommended', 'recommended settings', 'best settings',
      'optimal', 'configure', 'setup settings', 'settings',
      'kya settings', 'sahi settings', 'अनुशंसित', 'सेटिंग', 'चेकलिस्ट',
    ],
    title: {
      en: 'Recommended camera settings (quick reference)',
      hi: 'अनुशंसित कैमरा settings (quick reference)',
    },
    intro: {
      en: 'Set these on the stream the AI uses, for the most reliable detection:',
      hi: 'सबसे reliable detection के लिए AI वाली stream पर ये settings रखें:',
    },
    table: {
      headers: {
        en: ['Setting', 'Recommended', 'Why'],
        hi: ['Setting', 'Recommended', 'क्यों'],
      },
      rows: {
        en: [
          ['Codec', 'H.264 (plain)', 'Widely supported; avoids Smart-Codec long GOPs'],
          ['H.264+ / H.265+ / Smart Codec', 'OFF', 'Long/variable GOP starves the AI of frames'],
          ['I-Frame Interval (GOP)', '= FPS, or FPS / 2', 'Sets how many keyframes/sec the AI gets'],
          ['Bitrate Type', 'CBR (Constant)', 'Steady, even frame timing'],
          ['Resolution', '1080p main / 480p sub', 'Sub stream for AI keeps bandwidth low'],
          ['Transport', 'TCP', 'More reliable than UDP across networks'],
          ['Frame Rate', '15–25 fps', 'Enough for detection without wasting bandwidth'],
        ],
        hi: [
          ['Codec', 'H.264 (plain)', 'व्यापक रूप से supported; Smart-Codec के लंबे GOP से बचाता है'],
          ['H.264+ / H.265+ / Smart Codec', 'OFF', 'लंबा/variable GOP AI को frames से वंचित करता है'],
          ['I-Frame Interval (GOP)', '= FPS, या FPS / 2', 'तय करता है AI को प्रति सेकंड कितने keyframe मिलेंगे'],
          ['Bitrate Type', 'CBR (Constant)', 'स्थिर, समान frame timing'],
          ['Resolution', '1080p main / 480p sub', 'AI के लिए sub stream bandwidth कम रखता है'],
          ['Transport', 'TCP', 'networks पर UDP से ज़्यादा reliable'],
          ['Frame Rate', '15–25 fps', 'detection के लिए पर्याप्त, bandwidth बर्बाद नहीं'],
        ],
      },
    },
  },
];

// UI chrome strings — bilingual from one source.
export const GUIDE_UI: Record<string, LangText> = {
  title: { en: 'Guide & Troubleshooting', hi: 'गाइड और समस्या-समाधान' },
  subtitle: {
    en: 'Step-by-step fixes for common camera and stream issues.',
    hi: 'आम camera और stream समस्याओं के step-by-step समाधान।',
  },
  symptomLabel: { en: 'Symptom', hi: 'लक्षण' },
  causeLabel: { en: 'Cause', hi: 'कारण' },
  fixLabel: { en: 'Steps', hi: 'तरीका' },
  askTitle: { en: 'Describe your problem', hi: 'अपनी समस्या लिखें' },
  askPlaceholder: {
    en: 'e.g. fps is not coming right / camera won’t connect',
    hi: 'जैसे: fps सही नहीं आ रही / कैमरा connect नहीं हो रहा',
  },
  askButton: { en: 'Find fix', hi: 'समाधान खोजें' },
  askNoMatch: {
    en: 'No exact match — browse the topics below, or email support.',
    hi: 'कोई पक्का match नहीं मिला — नीचे दिए टॉपिक देखें, या support को email करें।',
  },
  askMatchLead: { en: 'Best match for your question:', hi: 'आपके सवाल का सबसे सही जवाब:' },
};
